/**
 * Native drag-and-drop payloads for the Cost Scheme board.
 *
 * Three drag sources share one MIME key: a placeholder dragged from the palette (`palette`), an
 * accommodation booking dragged within its strip (`accom-strip`), and a multi-day service dragged within
 * its strip (`svc-strip`). Drop targets read the payload and branch on `source`. Payloads are
 * JSON-serialized because the native DataTransfer only carries strings.
 */
import type { PlaceholderType, TransportKind } from '@/api/generated/schemas';

/** MIME key used for all native drag payloads on the board. */
export const DND_MIME = 'application/x-cost-scheme';

/**
 * Payload for a placeholder dragged from the palette into the osa. `placeholderId` is `null` for a blank
 * box dragged from a group's footer (a part created without a source placeholder).
 */
export interface PalettePayload {
  source: 'palette';
  placeholderId: string | null;
  type: PlaceholderType;
  transportKind: TransportKind | null;
  name: string;
}

/** Payload for an accommodation booking dragged within the accommodation strip. */
export interface AccomStripPayload {
  source: 'accom-strip';
  uid: string;
}

/** Payload for a multi-day service dragged within the services strip. */
export interface ServiceStripPayload {
  source: 'svc-strip';
  uid: string;
}

export type DragPayload = PalettePayload | AccomStripPayload | ServiceStripPayload;

export function writeDrag(
  event: React.DragEvent,
  payload: DragPayload,
  effect: 'copy' | 'move'
): void {
  event.dataTransfer.setData(DND_MIME, JSON.stringify(payload));
  event.dataTransfer.effectAllowed = effect;
}

export function readDrag(event: React.DragEvent): DragPayload | null {
  const raw = event.dataTransfer.getData(DND_MIME);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DragPayload;
  } catch {
    return null;
  }
}
